import { Loader, Check, Circle, AlertCircle } from 'lucide-react'
import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import type { Progress } from '../../../shared/types/chat'

interface Props {
  progress: Progress
}

/**
 * Multi-step progress tracker -- shows the AI working through a task.
 * Read-only; the AI updates steps as it progresses.
 */
export default function ProgressCard({ progress }: Props) {
  const { t } = useTranslation('chat')
  const doneCount = progress.steps.filter(s => s.status === 'done').length
  const total = progress.steps.length
  const pct = Math.round((doneCount / total) * 100)
  const hasError = progress.steps.some(s => s.status === 'error')
  const allDone = doneCount === total

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`glass-card rounded-xl overflow-hidden border-l-4 ${
        hasError ? 'border-rose bg-rose/5' : allDone ? 'border-emerald bg-emerald/5' : 'border-gold bg-gold/5'
      }`}
    >
      <div className="p-4">
        {/* Header + progress bar */}
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-ivory font-medium text-sm">{progress.title}</h4>
          <span className={`text-xs font-semibold ${
            hasError ? 'text-rose' : allDone ? 'text-emerald' : 'text-gold'
          }`}>
            {hasError ? t('progress.error') : allDone ? t('progress.done') : `${pct}%`}
          </span>
        </div>

        {progress.subtitle && (
          <p className="text-pearl/50 text-xs mb-3">{progress.subtitle}</p>
        )}

        {/* Progress bar */}
        <div className="h-1 rounded-full bg-white/5 mb-4 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className={`h-full rounded-full ${
              hasError ? 'bg-rose' : allDone ? 'bg-emerald' : 'bg-gold'
            }`}
          />
        </div>

        {/* Steps */}
        <div className="space-y-0">
          {progress.steps.map((step, i) => {
            const isLast = i === progress.steps.length - 1
            return (
              <div key={step.id} className="flex gap-3">
                {/* Vertical line + icon */}
                <div className="flex flex-col items-center">
                  <div className="flex items-center justify-center w-5 h-5 shrink-0">
                    {step.status === 'done' && (
                      <div className="w-4 h-4 rounded-full bg-emerald/20 flex items-center justify-center">
                        <Check size={10} className="text-emerald" />
                      </div>
                    )}
                    {step.status === 'active' && (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 1.5, ease: 'linear' }}
                        className="w-4 h-4 flex items-center justify-center"
                      >
                        <Loader size={12} className="text-gold" />
                      </motion.div>
                    )}
                    {step.status === 'pending' && (
                      <Circle size={10} className="text-pearl/45" />
                    )}
                    {step.status === 'error' && (
                      <div className="w-4 h-4 rounded-full bg-rose/20 flex items-center justify-center">
                        <AlertCircle size={10} className="text-rose" />
                      </div>
                    )}
                  </div>
                  {!isLast && (
                    <div className={`w-px flex-1 min-h-[16px] ${
                      step.status === 'done' ? 'bg-emerald/20' : 'bg-white/5'
                    }`} />
                  )}
                </div>

                {/* Label + detail */}
                <div className={`pb-3 ${isLast ? 'pb-0' : ''}`}>
                  <span className={`text-xs ${
                    step.status === 'done' ? 'text-pearl/60' :
                    step.status === 'active' ? 'text-ivory font-medium' :
                    step.status === 'error' ? 'text-rose' :
                    'text-pearl/55'
                  }`}>
                    {step.label}
                  </span>
                  {step.detail && (
                    <p className="text-xs text-pearl/60 mt-0.5">{step.detail}</p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </motion.div>
  )
}
